<ng-container *ngIf="isSuperUser; else nonSuperDashboard">
  <div class="superuser-dashboard">
    <header class="superuser-header">
      <div>
        <h2>Business Dashboard</h2>
        <p>Kyra internal business visibility across customers, sales, revenue, renewals, and operations.</p>
      </div>
    </header>

    <div class="superuser-loading" *ngIf="superuserLoading">Loading business dashboard...</div>

    <div class="superuser-error" *ngIf="!superuserLoading && superuserError">
      <p>Unable to load business dashboard.</p>
      <button type="button" class="superuser-retry" (click)="loadSuperuserDashboard()">Retry</button>
    </div>

    <ng-container *ngIf="!superuserLoading && !superuserError">
      <section class="superuser-section">
        <div class="section-title">
          <h3>Business Summary</h3>
        </div>
        <div class="superuser-card-grid">
          <div
            class="superuser-card"
            *ngFor="let card of summaryCards"
            [class.superuser-card-clickable]="isCardClickable(card)"
            (click)="openSuperuserCard(card)">
            <span class="superuser-card-label">{{ card.label }}</span>
            <strong class="superuser-card-value">
              {{ card.label.includes('Amount') ? formatAmount(card.value) : (card.value || 0) }}
            </strong>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Revenue Analytics</h3>
        </div>
        <div class="superuser-analytics-grid">
          <div class="superuser-chart-card">
            <header class="top-header">
              <h3>Monthly Revenue Trend</h3>
              <p>Last 6 months</p>
            </header>
            <div class="superuser-chart-wrap" *ngIf="hasRevenueTrendData; else revenueTrendEmpty">
              <canvas
                baseChart
                [data]="revenueTrendChartData"
                [options]="superuserChartOptions"
                [type]="superuserLineChartType">
              </canvas>
            </div>
            <ng-template #revenueTrendEmpty>
              <div class="superuser-empty-state">No revenue trend data available.</div>
            </ng-template>
          </div>

          <div class="superuser-chart-card">
            <header class="top-header">
              <h3>Payment Collection Trend</h3>
              <p>Paid vs pending by month</p>
            </header>
            <div class="superuser-chart-wrap" *ngIf="hasPaymentTrendData; else paymentTrendEmpty">
              <canvas
                baseChart
                [data]="paymentTrendChartData"
                [options]="superuserChartOptions"
                [type]="superuserBarChartType">
              </canvas>
            </div>
            <ng-template #paymentTrendEmpty>
              <div class="superuser-empty-state">No payment trend data available.</div>
            </ng-template>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Lead Funnel</h3>
        </div>
        <div class="superuser-chart-card">
          <div class="superuser-chart-wrap">
            <canvas
              baseChart
              [data]="leadFunnelChartData"
              [options]="leadFunnelChartOptions"
              [type]="superuserBarChartType">
            </canvas>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Lead Source Performance</h3>
        </div>
        <div class="superuser-chart-card">
          <div class="superuser-chart-wrap" *ngIf="hasLeadSourceData; else leadSourceEmpty">
            <canvas
              baseChart
              [data]="leadSourceChartData"
              [options]="superuserChartOptions"
              [type]="superuserBarChartType">
            </canvas>
          </div>
          <ng-template #leadSourceEmpty>
            <div class="superuser-empty-state">No lead source data available.</div>
          </ng-template>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Attention</h3>
        </div>
        <div class="superuser-card-grid">
          <div
            class="superuser-card superuser-attention-card"
            *ngFor="let card of attentionCards"
            [class.superuser-card-clickable]="isCardClickable(card)"
            (click)="openSuperuserCard(card)">
            <span class="superuser-card-label">{{ card.label }}</span>
            <strong class="superuser-card-value">{{ card.value || 0 }}</strong>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Top Customers</h3>
        </div>
        <div class="superuser-table-card">
          <div class="responsive-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Contract Value</th>
                  <th>Paid</th>
                  <th>Pending</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngIf="!superuserDashboard.top_customers?.length">
                  <td colspan="5">No customer revenue data available.</td>
                </tr>
                <tr *ngFor="let customer of superuserDashboard.top_customers">
                  <td>{{ customer.customer }}</td>
                  <td>{{ formatAmount(customer.contract_value) }}</td>
                  <td>{{ formatAmount(customer.paid_amount) }}</td>
                  <td>{{ formatAmount(customer.pending_amount) }}</td>
                  <td><span class="superuser-badge">{{ formatStatus(customer.status) }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Subscription Health</h3>
        </div>
        <div class="superuser-health-grid">
          <div class="superuser-chart-card">
            <div class="superuser-chart-wrap" *ngIf="hasSubscriptionHealthData; else subscriptionHealthEmpty">
              <canvas
                baseChart
                [data]="subscriptionHealthChartData"
                [options]="superuserChartOptions"
                [type]="superuserDoughnutChartType">
              </canvas>
            </div>
            <ng-template #subscriptionHealthEmpty>
              <div class="superuser-empty-state">No subscription health data available.</div>
            </ng-template>
          </div>
          <div class="superuser-card-grid superuser-health-cards">
            <div class="superuser-card">
              <span class="superuser-card-label">Active</span>
              <strong class="superuser-card-value">{{ superuserDashboard.subscription_health?.active || 0 }}</strong>
            </div>
            <div class="superuser-card">
              <span class="superuser-card-label">Expiring Soon</span>
              <strong class="superuser-card-value">{{ superuserDashboard.subscription_health?.expiring_soon || 0 }}</strong>
            </div>
            <div class="superuser-card">
              <span class="superuser-card-label">Expired</span>
              <strong class="superuser-card-value">{{ superuserDashboard.subscription_health?.expired || 0 }}</strong>
            </div>
            <div class="superuser-card">
              <span class="superuser-card-label">Cancelled</span>
              <strong class="superuser-card-value">{{ superuserDashboard.subscription_health?.cancelled || 0 }}</strong>
            </div>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Sales Pipeline</h3>
        </div>
        <div class="superuser-card-grid">
          <div
            class="superuser-card"
            *ngFor="let card of salesCards"
            [class.superuser-card-clickable]="isCardClickable(card)"
            (click)="openSuperuserCard(card)">
            <span class="superuser-card-label">{{ card.label }}</span>
            <strong class="superuser-card-value">{{ card.value || 0 }}</strong>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Sales & Installation Partners</h3>
        </div>
        <div class="superuser-card-grid">
          <div
            class="superuser-card"
            *ngFor="let card of salesInstallationPartnerCards"
            [class.superuser-card-clickable]="isCardClickable(card)"
            (click)="openSuperuserCard(card)">
            <span class="superuser-card-label">{{ card.label }}</span>
            <strong class="superuser-card-value">
              {{ card.label.includes('Revenue') ? formatAmount(card.value) : (card.value || 0) }}
            </strong>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Revenue / Subscriptions</h3>
        </div>
        <div class="superuser-card-grid">
          <div
            class="superuser-card"
            *ngFor="let card of revenueCards"
            [class.superuser-card-clickable]="isCardClickable(card)"
            (click)="openSuperuserCard(card)">
            <span class="superuser-card-label">{{ card.label }}</span>
            <strong class="superuser-card-value">
              {{ card.label.includes('Amount') || card.label.includes('Value') ? formatAmount(card.value) : (card.value || 0) }}
            </strong>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="section-title">
          <h3>Operations</h3>
        </div>
        <div class="superuser-card-grid">
          <div
            class="superuser-card"
            *ngFor="let card of operationsCards"
            [class.superuser-card-clickable]="isCardClickable(card)"
            (click)="openSuperuserCard(card)">
            <span class="superuser-card-label">{{ card.label }}</span>
            <strong class="superuser-card-value">{{ card.value || 0 }}</strong>
          </div>
        </div>
      </section>

      <section class="superuser-section">
        <div class="superuser-table-grid">
          <div class="superuser-table-card">
            <header class="top-header">
              <h3>Pending Payments</h3>
              <p>Oldest due items first</p>
            </header>
            <div class="responsive-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Amount Due</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Days Overdue</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="!superuserDashboard.pending_payments?.length">
                    <td colspan="6">No pending payments.</td>
                  </tr>
                  <tr *ngFor="let payment of superuserDashboard.pending_payments">
                    <td>
                      {{ payment.customer }}
                      <div class="superuser-muted">{{ payment.contact_person || '-' }}</div>
                    </td>
                    <td>{{ formatAmount(payment.amount_due) }}</td>
                    <td>{{ payment.due_date | date: 'dd/MM/yyyy' }}</td>
                    <td><span class="superuser-badge">{{ formatStatus(payment.subscription_status) }}</span></td>
                    <td>{{ payment.days_overdue || 0 }}</td>
                    <td>
                      <button type="button" class="superuser-link-button" (click)="openSubscriptionDetail(payment)">View</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="superuser-table-card">
            <header class="top-header">
              <h3>Renewals Due Soon</h3>
              <p>Next 30 days</p>
            </header>
            <div class="responsive-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Expiry Date</th>
                    <th>Renewal Date</th>
                    <th>Contract Value</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="!superuserDashboard.renewals_due_soon?.length">
                    <td colspan="6">No renewals due soon.</td>
                  </tr>
                  <tr *ngFor="let renewal of superuserDashboard.renewals_due_soon">
                    <td>
                      {{ renewal.customer }}
                      <div class="superuser-muted">{{ renewal.contact_person || '-' }}</div>
                    </td>
                    <td>{{ renewal.expiry_date | date: 'dd/MM/yyyy' }}</td>
                    <td>{{ renewal.renewal_date | date: 'dd/MM/yyyy' }}</td>
                    <td>{{ formatAmount(renewal.contract_value) }}</td>
                    <td><span class="superuser-badge">{{ formatStatus(renewal.status) }}</span></td>
                    <td>
                      <button type="button" class="superuser-link-button" (click)="openSubscriptionDetail(renewal)">View</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </ng-container>
  </div>
</ng-container>

<ng-template #nonSuperDashboard>
  <ng-container *ngIf="isSalesManager; else standardDashboard">
    <app-sales-manager-dashboard></app-sales-manager-dashboard>
  </ng-container>
</ng-template>

<ng-template #standardDashboard>
<div class="dashboard-wrapper">
  <button
    type="button"
    class="operational-attention-banner"
    *ngIf="staleUnmatchedCount > 0"
    (click)="goToStaleVehiclesReview()">
    <span class="attention-badge">Operational Review</span>
    <span class="attention-copy">
      <strong>{{ staleUnmatchedCount }} vehicle{{ staleUnmatchedCount === 1 ? '' : 's' }}</strong>
      pending exit verification for more than {{ staleUnmatchedThresholdHours }} hours.
    </span>
    <span class="attention-action">Review in Analytics →</span>
  </button>

  <div class="stats-grid">
    <!-- Vehicle Entries Card -->
    <div class="metric-card" (click)="navigateVehicleEntry('Entry')">
      <div class="gradient-border blue-gradient"></div>
      <div class="gradient-bg blue-gradient"></div>

      <div class="card-header">
        <div class="icon-wrapper blue-icon-bg">
          <!-- <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <polyline points="9,22 9,12 15,12 15,22" />
          </svg> -->
          <fa-icon [icon]="['fas', 'arrow-right-to-bracket']"></fa-icon>
        </div>
        <div *ngIf="cardData?.inPercentChange > 0" class="badge up">
          <svg class="arrow-up" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5,12 12,5 19,12"></polyline>
          </svg>
          +{{cardData?.inPercentChange}}%
        </div>
        <div *ngIf="cardData?.inPercentChange < 0" class="badge down">
          <svg class="arrow-down" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <polyline points="19,12 12,19 5,12"></polyline>
          </svg>
          {{cardData?.inPercentChange}}%
        </div>
      </div>

      <h3 class="card-title">Total Vehicle Entries</h3>
      <div class="card-value">
        <!-- {{ displayedCounts.entry | number }} -->
        {{cardData?.inCount | number}}

      </div>
      <p class="card-description">Vehicles entered today</p>

      <div class="decorative-circle blue-gradient"></div>
    </div>

    <!-- Vehicle Exits Card -->
    <div class="metric-card" (click)="navigateVehicleEntry('Exit')">
      <div class="gradient-border green-gradient"></div>
      <div class="gradient-bg green-gradient"></div>

      <div class="card-header">
        <div class="icon-wrapper green-icon-bg">
          <!-- <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M9 21H5a2 2 0 01-2-2V9l3-3h4l3 3v11a2 2 0 01-2 2h-4z" />
            <path d="M16 21h4a2 2 0 002-2V9l-3-3h-4l-3 3v11a2 2 0 002 2h4z" />
          </svg> -->
          <fa-icon [icon]="['fas', 'arrow-right-from-bracket']"></fa-icon>
        </div>
        <div *ngIf="cardData?.outPercentChange > 0" class="badge up">
          <svg class="arrow-up" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5,12 12,5 19,12"></polyline>
          </svg>
          +{{cardData?.outPercentChange}}%
        </div>
        <div *ngIf="cardData?.outPercentChange < 0" class="badge down">
          <svg class="arrow-down" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <polyline points="19,12 12,19 5,12"></polyline>
          </svg>
          {{cardData?.outPercentChange}}%
        </div>
      </div>

      <h3 class="card-title">Total Vehicle Exits</h3>
      <div class="card-value">
        <!-- 1,187 -->
        <!-- {{ displayedCounts.exit | number }} -->
        {{cardData?.outCount | number}}


      </div>
      <p class="card-description">Vehicles exited today</p>

      <div class="decorative-circle green-gradient"></div>
    </div>

    <!-- Verified Matches Card -->
    <div class="metric-card" (click)="navigateVehicleEntry('Matched')">
      <div class="gradient-border emerald-gradient"></div>
      <div class="gradient-bg emerald-gradient"></div>

      <div class="card-header">
        <div class="icon-wrapper emerald-icon-bg">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <polyline points="22,4 12,14.01 9,11.01" />
          </svg>
        </div>
        <!-- <div class="badge up">
                    <svg class="arrow-up" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <line x1="12" y1="19" x2="12" y2="5"></line>
                        <polyline points="5,12 12,5 19,12"></polyline>
                    </svg>
                    +15%
                </div> -->
        <div *ngIf="cardData?.matchedPercentChange > 0" class="badge up">
          <svg class="arrow-up" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5,12 12,5 19,12"></polyline>
          </svg>
          +{{cardData?.matchedPercentChange}}%
        </div>
        <div *ngIf="cardData?.matchedPercentChange < 0" class="badge down">
          <svg class="arrow-down" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <polyline points="19,12 12,19 5,12"></polyline>
          </svg>
          {{cardData?.matchedPercentChange}}%
        </div>
      </div>

      <h3 class="card-title">Matched Vehicles</h3>
      <div class="card-value">
        <!-- 1,156 -->
        <!-- {{ displayedCounts.matched | number }} -->
        {{cardData?.matchedCount | number}}



      </div>
      <p class="card-description">Successfully matched with database</p>

      <div class="decorative-circle emerald-gradient"></div>
    </div>

    <!-- Unrecognized Plates Card -->
    <div class="metric-card" (click)="navigateVehicleEntry('Unmatched')">
      <div class="gradient-border amber-gradient"></div>
      <div class="gradient-bg amber-gradient"></div>

      <div class="card-header">
        <div class="icon-wrapper amber-icon-bg">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div *ngIf="cardData?.unmatchedPercentChange > 0" class="badge up">
          <svg class="arrow-up" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5,12 12,5 19,12"></polyline>
          </svg>
          +{{cardData?.unmatchedPercentChange}}%
        </div>
        <div *ngIf="cardData?.unmatchedPercentChange < 0" class="badge down">
          <svg class="arrow-down" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <polyline points="19,12 12,19 5,12"></polyline>
          </svg>
          {{cardData?.unmatchedPercentChange}}%
        </div>

      </div>

      <h3 class="card-title">Unmatched Vehicles</h3>
      <div class="card-value">
        <!-- 78 -->
        <!-- {{ displayedCounts.unmatched | number }} -->
        {{cardData?.unmatchedCount | number}}


      </div>
      <p class="card-description">Low confidence or unmatched</p>

      <div class="decorative-circle amber-gradient"></div>
    </div>
  </div>

  <div class="zone-utilization-grid">
    <div class="zone-util-card">
      <span>Capacity</span>
      <strong>{{ zoneOccupancy.capacity | number }}</strong>
    </div>
    <div class="zone-util-card">
      <span>Occupied</span>
      <strong>{{ zoneOccupancy.occupied | number }}</strong>
    </div>
    <div class="zone-util-card">
      <span>Available</span>
      <strong>{{ zoneOccupancy.available | number }}</strong>
    </div>
    <div class="zone-util-card">
      <span>Utilization</span>
      <strong>{{ zoneOccupancy.occupancy_percent }}%</strong>
    </div>
  </div>

  <div class="dashboard-visuals">
    <div class="chart-container">
      <!-- <div class="header">
        <h3>Vehicle Movement Statistics</h3>
       
      </div> -->
      <header class="top-header">
        <h3>Vehicle Movement Statistics</h3>

      </header>
      <div class="filter-buttons">
        <button *ngFor="let range of timeRanges" [class.active]="range.value === selectedRange"
          (click)="updateChart(range.value)">
          {{ range.label }}
        </button>
      </div>

      <canvas baseChart [data]="chartData" [options]="chartOptions" [type]="barChartType">
      </canvas>
    </div>

    <div class="camera-feed">
      <header class="top-header">
        <h3>Camera Feed</h3>

      </header>
      <div class="header">
        <span class="toggle-title">Video Stream</span>
        <label class="switch">
          <input type="checkbox" (change)="toggleMedia()" />
          <span class="slider round"></span>
          <div class="tooltip" [class.show]="showTooltip">
            {{ isVideoMode ? 'Switch to Image' : 'Switch to Video' }}
          </div>
          <!-- <div class="tooltip" [class.show]="showTooltip">Switch to {{ isVideoMode ? 'Image' : 'Video' }}</div> -->
        </label>
      </div>

      <div class="feed-box" *ngIf="!isVideoMode">
        <!-- Image Slideshow Container -->
        <div class="image-slideshow-container">
          <div class="camera-box" [ngClass]="{ 'tooltip-visible': !isVideoMode, 'tooltip-hidden': isVideoMode }">
            <img *ngIf="getCurrentImage()" [src]="getCurrentImage().url" alt="camera feed image" class="slideshow-image"
              (load)="onImageLoad()" (error)="onImageError($event, 'slideshow')">
            <div *ngIf="imageLoadFailed" class="image-fallback-text">{{ failedImageLabel }}</div>
          </div>

          <!-- Navigation Arrows (show only if multiple images) -->
          <div *ngIf="cameraFeedImages.length > 1" class="image-nav-arrows">
            <button class="nav-arrow prev-arrow" (click)="previousImage()" title="Previous image">
              <fa-icon [icon]="['fas', 'angle-left']"></fa-icon>
            </button>
            <button class="nav-arrow next-arrow" (click)="nextImage()" title="Next image">
              <fa-icon [icon]="['fas', 'angle-right']"></fa-icon>
            </button>
          </div>

          <!-- Image Indicators (show only if multiple images) -->
          <!-- <div *ngIf="cameraFeedImages.length > 1" class="image-indicators">
            <span *ngFor="let image of cameraFeedImages; let i = index" class="indicator-dot"
              [class.active]="i === currentImageIndex" (click)="currentImageIndex = i"
              [title]="image.direction + ' image'">
              {{ image.direction.charAt(0) }}
            </span>
          </div> -->
        </div>
      </div>
      <!-- Video player -->
      <div class="feed-box" *ngIf="isVideoMode">
        <!-- <div class="plate-info">Plate: KA01AB1234 (96% confidence)</div> -->
        <div class="camera-box" [ngClass]="{ 'tooltip-visible': isVideoMode, 'tooltip-hidden': !isVideoMode }">
          <img [src]="videoPlaceholderUrl" alt="No video available" (load)="onImageLoad()"
            (error)="onImageError($event, 'video')">
          <div *ngIf="imageLoadFailed" class="image-fallback-text">{{ failedImageLabel }}</div>

          <!-- <video width="100%" controls muted>
            <source src="5309381-hd_1920_1080_25fps.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video> -->
        </div>
      </div>
      <div class="feed-controls">
        <button (click)="refresh()"><fa-icon [icon]="['fas', 'refresh']"></fa-icon>Refresh</button>
        <!-- <button><i class="fas fa-camera-retro"></i> Snapshot</button>
        <button><i class="fas fa-cog"></i></button> -->
      </div>
        <div class="camera-info"><strong> Vehicle Number: </strong>
            <span [appVehicleNumberCopy]="mostRecentActivity?.vehicle_number">{{ mostRecentActivity?.vehicle_number }}</span><br><strong>
          Camera: </strong>
          <span class="camera-display-inline">
            <span *ngFor="let camera of getCameraDisplayParts(mostRecentActivity)" class="camera-display-chip">
              <span *ngIf="camera.label" class="camera-direction-badge">{{ camera.label }}</span>
              <span class="camera-esn-value">{{ camera.value }}</span>
            </span>
            <span *ngIf="!getCameraDisplayParts(mostRecentActivity).length">-</span>
          </span><br><strong>Created At:
        </strong>{{mostRecentActivity?.created_at | date: 'medium'}}<br></div>
    </div>
  </div>
  <div class="dashboard-table-container">
    <header class="top-header">
      <h3>Today's Vehicle Activity Log</h3>
        <p>Today's activity including overnight vehicle sessions</p>
    </header>
    <div class="tab-head">
      <div class="left-actions">
        <div class="date-filter">
          <button type="button" class="date-filter-button" (click)="toggleDateFilter()" title="Select Date Range">
            <fa-icon [icon]="['fas', 'calendar-days']" class="date-filter-icon"></fa-icon>
            <span class="date-filter-label">Date Range</span>
          </button>
          <div class="date-filter-menu" *ngIf="showDateFilter">
            <button
              type="button"
              *ngFor="let range of activityDateRanges"
              [class.active]="range.value === activityDateRange"
              (click)="selectActivityDateRange(range.value)">
              {{ range.label }}
            </button>
          </div>
        </div>

        <div class="column-settings">
          <button type="button" class="column-settings-button" (click)="toggleColumnSettings()" title="Customize Columns">
            <fa-icon [icon]="['fas', 'gear']"></fa-icon>
            <span class="column-settings-label">Columns</span>
          </button>
          <div class="column-settings-menu" *ngIf="showColumnSettings">
            <label *ngFor="let column of dashboardColumns">
              <input
                type="checkbox"
                [checked]="column.visible"
                (change)="updateDashboardColumn(column, $event)" />
              <span>{{ column.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="right-actions">
        <button class="export-button" (click)="exportToExcel()">
          <fa-icon [icon]="['fas', 'file-excel']" class="fa-thin" style="color: white;font-size: 15px;"></fa-icon>
          EXPORT EXCEL
        </button>
      </div>
    </div>

    <div class="table-filters">
      <!-- <input type="text" placeholder="Search ..." /> -->
      <!-- <div class="search-bar"> -->
      <div>
        <app-generic-search 
        [apiUrl]="'dashboard/today-activity/search_suggestion'" 
        [resultsApiUrl]="'dashboard/stats/'" 
        [method]="'POST'"
        [searchParamName]="'search_text'"
        [pageSize]="10" 
        [minChars]="2" 
        (resultsFetched)="onResults($event)"
        [extraBody]="dashboardSearchExtraBody">
        </app-generic-search>
      </div>
      <!-- </div> -->
      <select id="cameraEsnFilter" [(ngModel)]="selectedCameraFilter" (change)="selectCameraFilter()" title="Filter by Camera ESN">
        <option value="">All Cameras</option>
        <option *ngFor="let esn of availableCameras" [value]="esn">{{ esn }}</option>
      </select>
      <select id="vehicleStatus" [(ngModel)]="vehicleStatus" (change)="selectVehilceStatus()">
        <option value="">All Status</option>
        <option value="Matched">Matched</option>
        <option value="Unmatched">Unmatched</option>
        <option value="Hotlisted">HotListed</option>
        <option value="Blacklisted">BlackListed</option>
      </select>
      <!-- <select id="vehicleStatus" [(ngModel)]="vehicleStatus" (change)="selectVehilceStatus()" >
      <option *ngFor="let option of ['All Status', 'Matched', 'Unmatched', 'Hotlisted', 'BlackListed']" [value]="All Status ? '': option">
        {{ option }}
      </option>
    </select> -->

    </div>

    <div class="responsive-table-wrapper">
      <table>
        <thead>
          <tr>
            <th>S.No</th>
            <th (click)="sortData('vehicle_number')" (mouseenter)="hoveredColumn = 'vehicleNumber'"
              (mouseleave)="hoveredColumn = ''">
              Vehicle Number
              <span *ngIf="sortDirection === 'asc'" class="sort-icons"
                [class.visible]="hoveredColumn === 'vehicleNumber'">
                <fa-icon [icon]="['fas', 'arrow-up']"
                  [class.active]="sortColumn === 'vehicle_number' && sortDirection === 'asc'"></fa-icon>
              </span>
              <span *ngIf="sortDirection === 'desc'" class="sort-icons"
                [class.visible]="hoveredColumn === 'vehicleNumber'">
                <fa-icon [icon]="['fas', 'arrow-down']"
                  [class.active]="sortColumn === 'vehicle_number' && sortDirection === 'desc'"></fa-icon>
              </span>
            </th>
            <th (click)="sortData('entry_time')" (mouseenter)="hoveredColumn = 'entryTime'"
              (mouseleave)="hoveredColumn = ''">
              Entry Time
              <span *ngIf="sortDirection === 'asc'" class="sort-icons" [class.visible]="hoveredColumn === 'entryTime'">
                <fa-icon [icon]="['fas', 'arrow-up']"
                  [class.active]="sortColumn === 'entry_time' && sortDirection === 'asc'"></fa-icon>
              </span>
              <span *ngIf="sortDirection === 'desc'" class="sort-icons" [class.visible]="hoveredColumn === 'entryTime'">
                <fa-icon [icon]="['fas', 'arrow-down']"
                  [class.active]="sortColumn === 'entry_time' && sortDirection === 'desc'"></fa-icon>
              </span>
            </th>
            <th (click)="sortData('exit_time')" (mouseenter)="hoveredColumn = 'exitTime'"
              (mouseleave)="hoveredColumn = ''">
              Exit Time
              <span *ngIf="sortDirection === 'asc'" class="sort-icons" [class.visible]="hoveredColumn === 'exitTime'">
                <fa-icon [icon]="['fas', 'arrow-up']"
                  [class.active]="sortColumn === 'exit_time' && sortDirection === 'asc'"></fa-icon>
              </span>
              <span *ngIf="sortDirection === 'desc'" class="sort-icons" [class.visible]="hoveredColumn === 'exitTime'">
                <fa-icon [icon]="['fas', 'arrow-down']"
                  [class.active]="sortColumn === 'exit_time' && sortDirection === 'desc'"></fa-icon>
              </span>
            </th>
            <th *ngIf="isDashboardColumnVisible('parking_time')" (click)="sortData('parking_time')" (mouseenter)="hoveredColumn = 'parking_time'"
              (mouseleave)="hoveredColumn = ''">
              Parking Time
              <span *ngIf="sortDirection === 'asc'" class="sort-icons" [class.visible]="hoveredColumn === 'confidence'">
                <fa-icon [icon]="['fas', 'arrow-up']"
                  [class.active]="sortColumn === 'parking_time' && sortDirection === 'asc'"></fa-icon>
              </span>
              <span *ngIf="sortDirection === 'desc'" class="sort-icons"
                [class.visible]="hoveredColumn === 'confidence'">
                <fa-icon [icon]="['fas', 'arrow-down']"
                  [class.active]="sortColumn === 'confidence' && sortDirection === 'desc'"></fa-icon>
              </span>
            </th>

            <th *ngIf="isDashboardColumnVisible('camera')" (click)="sortData('camera_esn')" (mouseenter)="hoveredColumn = 'camera'"
              (mouseleave)="hoveredColumn = ''">
              Camera
              <span *ngIf="sortDirection === 'asc'" class="sort-icons" [class.visible]="hoveredColumn === 'camera'">
                <fa-icon [icon]="['fas', 'arrow-up']"
                  [class.active]="sortColumn === 'camera_esn' && sortDirection === 'asc'"></fa-icon>
              </span>
              <span *ngIf="sortDirection === 'desc'" class="sort-icons" [class.visible]="hoveredColumn === 'camera'">
                <fa-icon [icon]="['fas', 'arrow-down']"
                  [class.active]="sortColumn === 'camera_esn' && sortDirection === 'desc'"></fa-icon>
              </span>
            </th>
            <th>
              Status
            </th>
            <th *ngIf="isDashboardColumnVisible('images')">Vehicle Images</th>
            <!-- <th>EXIT Image</th> -->
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let vehicle of todaysActivity; let i = index">
            <td>{{ i + 1 + (currentPage - 1) * recordsPerPage }}</td>
              <td [appVehicleNumberCopy]="vehicle.vehicle_number">{{ vehicle.vehicle_number }}</td>
            <!-- <td>{{ vehicle.entry_time || '-'}}</td>
          <td>{{ vehicle.exit_time || '-' }}</td> -->
            <td>
                <div class="session-time-cell">
                <span class="session-time-value">
                  {{ vehicle.entry_time && vehicle.entry_time !== '-' ? (vehicle.entry_time | date: 'hh:mm:ss a') : '-' }}
                </span>
                    <span
                            *ngIf="hasCrossDayEntryExit(vehicle) && vehicle.entry_time && vehicle.entry_time !== '-'"
                            class="session-date-label">
                  {{ vehicle.entry_time | date: 'd MMM' }}
                </span>
                </div>
            </td>
            <td>
                <div class="session-time-cell">
                <span class="session-time-value">
                  {{ vehicle.exit_time && vehicle.exit_time !== '-' ? (vehicle.exit_time | date: 'hh:mm:ss a') : '-' }}
                </span>
                    <span
                            *ngIf="hasCrossDayEntryExit(vehicle) && vehicle.exit_time && vehicle.exit_time !== '-'"
                            class="session-date-label">
                  {{ vehicle.exit_time | date: 'd MMM' }}
                </span>
                </div>
            </td>
            <td *ngIf="isDashboardColumnVisible('parking_time')">{{ vehicle.parking_time_str}}</td>
            <td *ngIf="isDashboardColumnVisible('camera')">
              <div class="camera-display-stack">
                <span *ngFor="let camera of getCameraDisplayParts(vehicle)" class="camera-display-chip">
                  <span *ngIf="camera.label" class="camera-direction-badge">{{ camera.label }}</span>
                  <span class="camera-esn-value">{{ camera.value }}</span>
                </span>
                <span *ngIf="!getCameraDisplayParts(vehicle).length">-</span>
              </div>
            </td>
            <!-- <td>{{ vehicle.confidence}}%</td> -->

            <td>
              <!-- <div > -->
              <span *ngFor="let status of vehicle.status" class="status" [ngClass]="status.toLowerCase()">{{ status
                }}</span>
              <!-- </div> -->
            </td>
            <td *ngIf="isDashboardColumnVisible('images')">
              <button [disabled]="!vehicle.entry_vehicle_image_url && !vehicle.exit_vehicle_image_url"
                [style.opacity]="(vehicle.entry_vehicle_image_url || vehicle.exit_vehicle_image_url) ? 1 : 0.5"
                (click)="viewImage(vehicle.entry_vehicle_image_url, vehicle.exit_vehicle_image_url)"
                title="View entry and exit images">
                <fa-icon [icon]="['fas', 'eye']" class="fa-light"></fa-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="footer">
        <div class="total-records">Total Records: {{ totalRecords }}</div>
        <div class="pagination-controls">
          <label for="recordsPerPage">Records per page </label>
          <select id="recordsPerPage" [(ngModel)]="recordsPerPage" (change)="selectPageSize()">
              <option *ngFor="let option of pageSizeOptions" [value]="option">{{ option }}</option>
          </select>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button (click)="previous()" [disabled]="!previousPage" class="change-page" title="Previous page">
            <fa-icon [icon]="['fas', 'angle-left']" class="fa-light"></fa-icon>
          </button>
          <button (click)="next()" [disabled]="!nextPage" class="change-page" title="Next page">
            <fa-icon [icon]="['fas', 'angle-right']" class="fa-light" style="background-color: none;"></fa-icon>
          </button>
        </div>
      </div>
    </div>
    <!-- <div class="pagination">
    <span>Showing 5 of 5 vehicles</span>
    <button class="btn secondary">Previous</button>
    <button class="btn">Next</button>
  </div> -->
  </div>


  <!-- Modal Viewer -->
  <app-image-viewer [images]="selectedImages" [show]="showModal" [closeModal]="closeModal"></app-image-viewer>
</div>
</ng-template>
